import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { ResolvedDataset } from "../models/mail-template";
import { useAppSelector } from "../store";
import { companyActions, staticTestFields, STICKY_TEST_FIELDS } from "../store/company-slice";
import { fetchTemplateVariables } from "../utils/http/template-variables";

export interface TemplateTestFields {
  fields: Record<string, unknown>;
  datasets: ResolvedDataset[];
  datasetName: string | null;
  // Identifies the template and dataset the fields belong to. Edits are tagged with it and
  // dropped once it changes, and it is what the editor reseeds on.
  editKey: string;
  setDatasetName: (name: string | null) => void;
  setEditedText: (text: string) => void;
  isLoading: boolean;
  error: unknown;
}

const NO_DATASETS: ResolvedDataset[] = [];

/**
 * The single source of truth for the fields used to render the preview and to send a test
 * mail: the backend's dataset for this template, with the user's edits on top. Edits belong
 * to one template and dataset and are forgotten on leaving it. A null template key -- bulk
 * sending, where there is no single template -- falls back to the generic static fields.
 */
export function useTemplateTestFields(templateKey: string | null): TemplateTestFields {
  const dispatch = useDispatch();
  const companyId = useAppSelector((state) => state.company.activeCompany?.id);
  const edited = useAppSelector((state) => state.company.mailTemplateTestData.editedFields);
  const sticky = useAppSelector((state) => state.company.mailTemplateTestData.stickyFields);
  const datasetName = useAppSelector((state) => state.company.mailTemplateTestData.datasetName);

  const { data, isLoading, error } = useQuery({
    queryKey: ["templateVariables", companyId, templateKey],
    queryFn: ({ signal }) => fetchTemplateVariables({ signal, templateKey: templateKey as string }),
    enabled: !!templateKey && !!companyId,
    retry: false,
  });

  const datasets = data?.datasets ?? NO_DATASETS;

  // A dataset name is only meaningful within one template, so a name carried over from a
  // previous template that this one does not have falls back to the default.
  const dataset = useMemo(
    () =>
      datasets.find((d) => d.name === datasetName) ?? datasets.find((d) => d.is_default) ?? datasets[0],
    [datasets, datasetName],
  );

  // The dataset, carrying over the delivery settings from wherever they were last set.
  const baselineDataset = dataset?.fields ?? staticTestFields;
  const baseline = useMemo(
    () => ({ ...baselineDataset, ...sticky }),
    [baselineDataset, sticky],
  );
  const editKey = `${templateKey ?? ""}::${dataset?.name ?? ""}`;

  // Edits tagged with a different key belong to a template or dataset we have since left,
  // so they are ignored rather than applied here.
  const fields = useMemo(() => {
    if (edited?.key !== editKey) return baseline;
    try {
      const parsed = JSON.parse(edited.text);
      return parsed && typeof parsed === "object" ? parsed : baseline;
    } catch {
      // Text mid-edit is unparseable all the time; render the dataset rather than nothing.
      return baseline;
    }
  }, [edited, editKey, baseline]);

  const setDatasetName = useCallback(
    (name: string | null) => dispatch(companyActions.setTestDatasetName(name)),
    [dispatch],
  );
  const setEditedText = useCallback(
    (text: string) => {
      dispatch(companyActions.setEditedTestFields({ key: editKey, text }));
      try {
        const parsed = JSON.parse(text);
        if (!parsed || typeof parsed !== "object") return;
        dispatch(
          companyActions.setStickyTestFields(
            Object.fromEntries(
              STICKY_TEST_FIELDS.filter((key) => key in parsed).map((key) => [key, parsed[key]]),
            ),
          ),
        );
      } catch {
        // Unparseable mid-edit text tells us nothing about the delivery settings; the last
        // parseable version stands.
      }
    },
    [dispatch, editKey],
  );

  return {
    fields,
    datasets,
    datasetName: dataset?.name ?? null,
    editKey,
    setDatasetName,
    setEditedText,
    isLoading,
    error,
  };
}
